import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Card, Row, Col, Typography, Space, Tag, Checkbox, Form, Input, DatePicker, TimePicker, message, Modal, Alert } from 'antd';
import { CheckCircleOutlined, PhoneOutlined, CalendarOutlined, StarOutlined, DollarOutlined, CloseOutlined, HomeOutlined, SafetyOutlined, ClockCircleOutlined, GoogleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trackCampaignAction, trackCampaignQuote, isFromCampaign, getCurrentCampaign } from '../../utils/utmTracking';
import { trackPageView } from '../../utils/analytics';
import { theme } from '../../styles/theme';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { loadGoogleMaps, isGoogleMapsReady } from '../../utils/googleMapsLoader';
import { calculateServicePrice } from '../../config/stripe';
import { createStripeCheckout } from '../../services/stripeService';
import { BookingModal } from '../../components/common/ui';
import dayjs from 'dayjs';

const { Title, Paragraph, Text } = Typography;

// Simple Payment Link Component
const PaymentLinkForm = ({ 
  totalPrice, 
  selectedServices, 
  campaignType, 
  formData,
  onBack
}) => {
  const handlePaymentLink = async () => {
    try {
      // Save the service request to Firebase first (as pending)
      const serviceData = {
        // Customer Information
        email: formData.email?.trim() || '',
        phone: formData.phone?.trim() || '',
        name: formData.name?.trim() || '',
        
        // Property Information
        address: formData.address?.trim() || '',
        specialInstructions: formData.instructions?.trim() || '',
        instructions: formData.instructions?.trim() || '',
        
        // Service Configuration
        selectedServices: selectedServices,
        exteriorServices: selectedServices,
        interiorServices: [],
        
        // Payment Information
        paymentStatus: 'pending',
        totalPrice: totalPrice,
        basePrice: campaignType === 'flyer-1' ? 299 : 99,
        addOnPrice: 50,
        
        // Scheduling Information
        preferredDate: formData.date?.format('YYYY-MM-DD') || '',
        preferredTime: formData.time?.format('HH:mm') || '',
        date: formData.date?.format('YYYY-MM-DD') || '',
        time: formData.time?.format('HH:mm') || '',
        startDate: formData.date?.format('YYYY-MM-DD') || '',
        
        // Service Status
        serviceFrequency: 'one-time',
        status: 'pending_payment',
        quoteType: 'campaign_special',
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Additional metadata
        source: 'campaign_landing',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        
        // UTM Campaign Data
        utmCampaign: isFromCampaign() ? getCurrentCampaign() : '',
        utmSource: isFromCampaign() ? getCurrentCampaign()?.source || '' : '',
        utmMedium: isFromCampaign() ? getCurrentCampaign()?.medium || '' : '',
        utmTerm: isFromCampaign() ? getCurrentCampaign()?.term || '' : '',
        campaignPage: 'free-inspect-weed'
      };

      // Save to Firebase
      const docRef = await addDoc(collection(db, 'quotes'), serviceData);
      
      // Create payment data for Stripe
      const paymentData = {
        amount: totalPrice * 100, // Convert to cents
        quoteId: docRef.id,
        services: selectedServices,
        campaignType: campaignType,
        customerEmail: formData.email?.trim() || '',
        customerName: formData.name?.trim() || '',
        description: `${campaignType === 'flyer-1' ? '4' : '3'} Services Package - ${selectedServices.length} services selected`
      };
      
      // Track the payment attempt
      trackCampaignAction('payment_checkout_started', { 
        services: selectedServices,
        totalPrice: totalPrice,
        quoteId: docRef.id
      });
      
      // Create Stripe Checkout session
      const result = await createStripeCheckout(paymentData);
      
      if (result.success) {
        // Stripe will handle the redirect to checkout
        console.log('Stripe checkout initiated successfully');
      }
      
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      message.error('Failed to create payment session. Please try again.');
    }
  };

  return (
    <div style={{ marginTop: '20px', textAlign: 'center' }}>
      <div style={{ marginBottom: '30px' }}>
        <Title level={4} style={{ marginBottom: '8px', fontFamily: theme.fonts.primary }}>
          Complete Your Payment
        </Title>
        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary, fontSize: '16px' }}>
          Total Amount: <strong style={{ color: theme.colors.primary, fontSize: '18px' }}>${totalPrice}</strong>
        </Text>
      </div>
      
      <div style={{ 
        background: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '12px', 
        marginBottom: '30px',
        border: '1px solid #e9ecef'
      }}>
        <Text style={{ fontFamily: theme.fonts.primary, fontSize: '14px', color: theme.colors.gray }}>
          You'll be redirected to Stripe's secure checkout page to complete your purchase.
          After successful payment, you'll be redirected back to our site with confirmation.
        </Text>
      </div>
      
      <div style={{ textAlign: 'center' }}>
        <Space size="large">
          <Button 
            size="large" 
            onClick={onBack}
            style={{ padding: '0 24px' }}
          >
            Back
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handlePaymentLink}
            style={{
              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              border: 'none',
              borderRadius: '16px',
              height: '56px',
              fontFamily: theme.fonts.primary,
              fontWeight: '700',
              fontSize: '18px',
              boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              padding: '0 24px'
            }}
          >
            <DollarOutlined style={{ marginRight: '8px' }} />
            Pay ${totalPrice} - Secure Checkout
          </Button>
        </Space>
      </div>
    </div>
  );
};

const FreeInspectWeedPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showSchedulingForm, setShowSchedulingForm] = useState(false);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  const [submittedQuoteId, setSubmittedQuoteId] = useState(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [campaignType, setCampaignType] = useState('free-inspect-weed');
  // Removed unnecessary payment form state

  // Google Places autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Payment processing states
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    // Get campaign type from localStorage
    const storedCampaignType = localStorage.getItem('campaign_type') || 'free-inspect-weed';
    setCampaignType(storedCampaignType);
    
    // Pre-select services based on campaign type
    if (storedCampaignType === 'flyer-1') {
      // Pre-select the 4 included services for flyer campaign
      setSelectedServices(['gutter-cleaning', 'weed-pulling', 'junk-hauling', 'hedge-clipping']);
    } else {
      setSelectedServices(['gutter-cleaning', 'weed-pulling', 'solar-cleaning']);
    }
    
    const pageTitle = storedCampaignType === 'flyer-1' ? 'Flyer Special - HSP' : 'Hangtag Special - HSP';
    const pageId = storedCampaignType === 'flyer-1' ? 'flyer-1-campaign' : 'free-inspect-weed-campaign';
    
    trackPageView(pageId, pageTitle);
    trackCampaignAction('campaign_page_view', { page: `${storedCampaignType}-landing` });
  }, []);

  // Initialize Google Maps
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMaps();
        console.log('Google Maps loaded successfully for FreeInspectWeedPage');
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    };

    initializeGoogleMaps();
  }, []);

  // Address handling with Google Places API
  const handleAddressChange = useCallback((e) => {
    const value = e.target.value;
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (value.length > 2 && isGoogleMapsReady()) {
      setAddressLoading(true);
      debounceRef.current = setTimeout(() => {
        const google = window.google;
        const service = new google.maps.places.AutocompleteService();
        const request = {
          input: value,
          types: ['address'],
          componentRestrictions: { country: 'us' }
        };
        
        service.getPlacePredictions(request, (predictions, status) => {
          setAddressLoading(false);
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setAddressSuggestions(predictions.map(prediction => prediction.description));
            setShowSuggestions(true);
          } else {
            setAddressSuggestions([]);
            setShowSuggestions(false);
          }
        });
      }, 300);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleSuggestionClick = useCallback((suggestion) => {
    form.setFieldsValue({ address: suggestion });
    setAddressSuggestions([]);
    setShowSuggestions(false);
  }, [form]);

  const handleAddressFocus = useCallback(() => {
    if (addressSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [addressSuggestions]);

  const handleAddressBlur = useCallback(() => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);

  const handleCallNow = () => {
    trackCampaignAction('phone_click', { source: 'campaign_landing' });
    window.open('tel:+18885046944', '_self');
  };

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    try {
      trackCampaignAction('google_signin_click', { 
        quoteId: submittedQuoteId,
        source: 'campaign_landing'
      });
      
      const result = await signInWithGoogle();
      if (result) {
        message.success('Welcome to HSP! You can now track your service request.');
        setShowSignupPrompt(false);
        form.resetFields();
        setSelectedServices([]);
        // Optionally navigate to user dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      message.error('Sign-in failed. You can still track your request via phone.');
    } finally {
      setIsSigningIn(false);
    }
  };

  // Dynamic service options based on campaign type
  const getServiceOptions = () => {
    if (campaignType === 'flyer-1') {
      return [
        { id: 'gutter-cleaning', name: 'Gutter Inspection & Cleaning', description: 'Clear debris from gutters and downspouts (1 story homes only - extra charge for 2 story homes)' },
        { id: 'weed-pulling', name: 'Weed Pulling', description: 'Remove weeds from garden beds and paved areas' },
        { id: 'junk-hauling', name: 'Junk Hauling', description: 'Removal of unwanted items and debris (up to 100 linear feet)' },
        { id: 'hedge-clipping', name: 'Small Hedge Clipping', description: 'Trimming and shaping hedges and shrubs' },
        { id: 'solar-cleaning', name: 'Solar Panel Cleaning', description: 'Clean solar panels for maximum efficiency' },
        { id: 'deck-cleaning', name: 'Deck Cleaning', description: 'Thorough cleaning of deck surfaces' },
        { id: 'pressure-washing', name: 'Pressure Washing', description: 'Clean driveways, patios, and siding' },
        { id: 'touchup-painting', name: 'Exterior Touch-up Painting', description: 'Minor exterior paint touch-ups' },
        { id: 'window-cleaning', name: 'Window Cleaning', description: 'Professional window cleaning service' },
        { id: 'fence-repair', name: 'Fence Repair & Staining', description: 'Repair and stain fence surfaces' },
        { id: 'roof-cleaning', name: 'Roof Cleaning', description: 'Safe roof cleaning and maintenance' },
        { id: 'driveway-sealing', name: 'Driveway Sealing', description: 'Seal and protect driveway surfaces' }
      ];
    } else {
      return [
        { id: 'gutter-cleaning', name: 'Gutter Inspection & Cleaning', description: 'Clear debris from gutters and downspouts' },
        { id: 'weed-pulling', name: 'Weed Pulling', description: 'Remove weeds from garden beds and paved areas' },
        { id: 'solar-cleaning', name: 'Solar Panel Cleaning', description: 'Clean solar panels for maximum efficiency' },
        { id: 'touchup-painting', name: 'Exterior Touch-up Painting', description: 'Minor exterior paint touch-ups' },
        { id: 'pressure-washing', name: 'Pressure Washing', description: 'Clean driveways, patios, and siding' },
        { id: 'deck-cleaning', name: 'Deck Cleaning', description: 'Thorough cleaning of deck surfaces' },
        { id: 'window-cleaning', name: 'Window Cleaning', description: 'Professional window cleaning service' },
        { id: 'fence-repair', name: 'Fence Repair & Staining', description: 'Repair and stain fence surfaces' },
        { id: 'roof-cleaning', name: 'Roof Cleaning', description: 'Safe roof cleaning and maintenance' },
        { id: 'driveway-sealing', name: 'Driveway Sealing', description: 'Seal and protect driveway surfaces' }
      ];
    }
  };

  const serviceOptions = getServiceOptions();

  const basePrice = campaignType === 'flyer-1' ? 299 : 99;
  const addOnPrice = 50;
  const includedServicesCount = campaignType === 'flyer-1' ? 4 : 3;

  const calculateTotal = () => {
    const selectedCount = selectedServices.length;
    if (selectedCount <= includedServicesCount) {
      return basePrice;
    } else {
      const addOns = selectedCount - includedServicesCount;
      return basePrice + (addOns * addOnPrice);
    }
  };

  const handleServiceChange = (serviceId, checked) => {
    // Don't allow deselecting included services (they're locked as part of the promotion)
    if (!checked && isServiceIncluded(serviceId)) {
      return; // Prevent deselecting included services
    }
    
    if (checked) {
      setSelectedServices([...selectedServices, serviceId]);
    } else {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
    }
  };

  // Helper function to determine if a service is included in the base package
  const isServiceIncluded = (serviceId) => {
    if (campaignType === 'flyer-1') {
      // For flyer-1 campaign, these 4 services are included
      return ['gutter-cleaning', 'weed-pulling', 'junk-hauling', 'hedge-clipping'].includes(serviceId);
    } else {
      // For free-inspect-weed campaign, these 3 services are included
      return ['gutter-cleaning', 'weed-pulling', 'solar-cleaning'].includes(serviceId);
    }
  };

  const handleFormSubmit = async (values) => {
    // Go directly to Stripe checkout after form validation
    try {
      setIsProcessingPayment(true);
      setPaymentError(null);
      
      // Save the service request to Firebase first (as pending)
      const serviceData = {
        // Customer Information
        email: values.email?.trim() || '',
        phone: values.phone?.trim() || '',
        name: values.name?.trim() || '',
        
        // Property Information
        address: values.address?.trim() || '',
        specialInstructions: values.instructions?.trim() || '',
        instructions: values.instructions?.trim() || '',
        
        // Service Configuration
        selectedServices: selectedServices,
        exteriorServices: selectedServices,
        interiorServices: [],
        
        // Payment Information
        paymentStatus: 'pending',
        totalPrice: calculateServicePrice(selectedServices, campaignType),
        basePrice: 99,
        addOnPrice: 50,
        
        // Scheduling Information
        preferredDate: values.date?.format('YYYY-MM-DD') || '',
        preferredTime: values.time?.format('HH:mm') || '',
        date: values.date?.format('YYYY-MM-DD') || '',
        time: values.time?.format('HH:mm') || '',
        startDate: values.date?.format('YYYY-MM-DD') || '',
        
        // Service Status
        serviceFrequency: 'one-time',
        status: 'pending_payment',
        quoteType: 'campaign_special',
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Additional metadata
        source: 'campaign_landing',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        
        // UTM Campaign Data
        utmCampaign: isFromCampaign() ? getCurrentCampaign() : '',
        utmSource: isFromCampaign() ? getCurrentCampaign()?.source || '' : '',
        utmMedium: isFromCampaign() ? getCurrentCampaign()?.medium || '' : '',
        utmTerm: isFromCampaign() ? getCurrentCampaign()?.term || '' : '',
        campaignPage: 'free-inspect-weed'
      };

      // Save to Firebase
      const docRef = await addDoc(collection(db, 'quotes'), serviceData);
      
      // Create payment data for Stripe
      const paymentData = {
        amount: calculateServicePrice(selectedServices, campaignType) * 100, // Convert to cents
        quoteId: docRef.id,
          services: selectedServices,
        campaignType: campaignType,
        customerEmail: values.email?.trim() || '',
        customerName: values.name?.trim() || '',
        description: `${campaignType === 'flyer-1' ? '4' : '3'} Services Package - ${selectedServices.length} services selected`
      };
      
      // Track the payment attempt
      trackCampaignAction('payment_checkout_started', { 
        services: selectedServices,
        totalPrice: calculateServicePrice(selectedServices, campaignType),
        quoteId: docRef.id
      });
      
      // Create Stripe Checkout session and redirect
      const result = await createStripeCheckout(paymentData);
      
      if (result.success) {
        // Stripe will handle the redirect to checkout
        console.log('Stripe checkout initiated successfully');
      }
      
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      setPaymentError('Failed to create payment session. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  // Payment success/error handling is now managed by Stripe webhooks
  // when users return from Stripe Payment Links

  // Payment processing is now handled by Stripe Payment Links
  // Service requests are saved as 'pending_payment' and updated via webhooks

  return (
    <>
      <style>
        {`
          .ant-modal-mask {
            z-index: 99998 !important;
          }
          .ant-modal-wrap {
            z-index: 99999 !important;
          }
          .ant-modal {
            z-index: 99999 !important;
          }
        `}
      </style>
      <Helmet>
        <title>$99 Hangtag Special - We're In Your Area | HSP</title>
        <meta name="description" content="$99 on-site visit covering gutter cleaning, weed pulling, solar panel cleaning, pressure washing, deck cleaning, and touch-up painting. We're in your area. Scan and book today." />
        <meta name="keywords" content="$99 special, gutter cleaning, weed pulling, solar cleaning, pressure washing, deck cleaning, touch-up painting, hangtag offer" />
        <meta property="og:title" content="$99 Hangtag Special - We're In Your Area" />
        <meta property="og:description" content="Flat $99 visit for select services. Limited availability. Book now." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        fontFamily: theme.fonts.primary
      }}>

        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '80px 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title level={2} style={{ 
              color: '#2c3e50', 
              fontSize: '2.5rem',
              marginBottom: '16px', 
              fontWeight: '700',
              fontFamily: theme.fonts.primary 
            }}>
              Select Your Services
            </Title>
            <Paragraph style={{ 
              color: theme.colors.gray, 
              fontSize: '1.1rem', 
              fontFamily: theme.fonts.primary,
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center'
            }}>
              {campaignType === 'flyer-1' ? (
                <>
                  <strong style={{ color: '#e74c3c' }}>4 services included for $299</strong> 
                  <span style={{ textDecoration: 'line-through', color: '#999', margin: '0 8px' }}>$399</span>
                  <span style={{ color: '#27ae60', fontWeight: '600' }}>Save $100!</span>
                </>
              ) : (
                <>
                  <strong style={{ color: '#e74c3c' }}>3 services included for $99</strong> 
                  <span style={{ textDecoration: 'line-through', color: '#999', margin: '0 8px' }}>$199</span>
                  <span style={{ color: '#27ae60', fontWeight: '600' }}>Save $100!</span>
                </>
              )}
            </Paragraph>
          </div>
            
          <Row gutter={[32, 32]} align="stretch" style={{ minHeight: window.innerWidth < 768 ? 'auto' : '600px' }}>
            {/* Left Column - Services */}
            <Col xs={24} lg={16}>
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: window.innerWidth < 768 ? 'auto' : '100%',
                justifyContent: window.innerWidth < 768 ? 'flex-start' : 'space-between'
              }}>
                <div style={{ marginBottom: '24px' }}>
                  <Title level={3} style={{ 
                    marginBottom: '16px', 
                    color: '#2c3e50',
                    fontSize: window.innerWidth < 768 ? '1.3rem' : '1.5rem',
                    fontWeight: '600'
                  }}>
                    Available Services
                  </Title>
            </div>
                <div style={{ flex: window.innerWidth < 768 ? 'none' : 1 }}>
                  <Row gutter={[16, 16]}>
            {serviceOptions.map((service, index) => {
              const isSelected = selectedServices.includes(service.id);
              const isIncluded = isServiceIncluded(service.id);
              const isAddOn = isSelected && !isIncluded;
              
              return (
                <Col xs={24} sm={24} md={12} key={service.id}>
                  <div 
                    style={{ 
                      padding: window.innerWidth < 768 ? '20px' : '16px',
                      background: isSelected 
                        ? (isIncluded 
                        ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' 
                            : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)')
                        : 'white',
                      borderRadius: '20px',
                      border: isSelected 
                        ? 'none' 
                        : '2px solid #e9ecef',
                      cursor: isIncluded ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      boxShadow: isSelected 
                        ? (isIncluded 
                        ? '0 15px 35px rgba(243, 156, 18, 0.3)' 
                            : '0 15px 35px rgba(108, 117, 125, 0.3)')
                        : '0 5px 15px rgba(0,0,0,0.08)',
                      transform: isSelected ? 'translateY(-5px)' : 'translateY(0)',
                      height: '100%'
                    }}
                    onClick={() => {
                      // Don't allow clicking on included services to deselect them
                      if (!isIncluded) {
                        handleServiceChange(service.id, !isSelected);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !isIncluded) {
                        e.target.style.transform = 'translateY(-3px)';
                        e.target.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !isIncluded) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.08)';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <Checkbox 
                        checked={isSelected}
                        disabled={isIncluded} // Disable checkbox for included services
                        onChange={(e) => handleServiceChange(service.id, e.target.checked)}
                        style={{ 
                          marginRight: '12px',
                          transform: 'scale(1.1)',
                          opacity: isIncluded ? 0.7 : 1
                        }}
                      />
                      <Text strong style={{ 
                        fontSize: '18px', 
                        color: isSelected ? 'white' : '#2c3e50', 
                        fontFamily: theme.fonts.primary,
                        lineHeight: '1.3'
                      }}>
                        {service.name}
                      </Text>
                    </div>
                    
                    {service.description && (
                      <Text style={{ 
                        color: isSelected ? 'rgba(255,255,255,0.9)' : theme.colors.gray, 
                        fontSize: '14px',
                        fontFamily: theme.fonts.primary,
                        lineHeight: '1.4',
                        marginBottom: '12px',
                        display: 'block'
                      }}>
                        {service.description}
                      </Text>
                    )}
                    
                    {isIncluded && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '15px', 
                        right: '15px',
                        background: '#27ae60',
                        color: 'white',
                        padding: '8px 14px',
                        borderRadius: '25px',
                        fontSize: '13px',
                        fontWeight: '700',
                        fontFamily: theme.fonts.primary,
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(39, 174, 96, 0.3)',
                        border: '2px solid white'
                      }}>
                        ✓ INCLUDED
                      </div>
                    )}
                    
                    {isAddOn && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '15px', 
                        right: '15px',
                        background: '#e74c3c',
                        color: 'white',
                        padding: '8px 14px',
                        borderRadius: '25px',
                        fontSize: '13px',
                        fontWeight: '700',
                        fontFamily: theme.fonts.primary,
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)',
                        border: '2px solid white'
                      }}>
                        +$50 ADD-ON
                      </div>
                    )}
                    
                    {/* Popular Badge for specific services */}
                    {(service.id === 'solar-cleaning' || service.id === 'deck-cleaning') && !isSelected && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '-8px', 
                        left: '10px',
                        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '700',
                        fontFamily: theme.fonts.primary,
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)',
                        border: '2px solid white',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        🔥 Popular
                      </div>
                    )}
                    
                    {!isSelected && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '20px', 
                        right: '20px',
                        background: '#3498db',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '700',
                        fontFamily: theme.fonts.primary
                      }}>
                        SELECT
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
                </div>
              </div>
            </Col>
            
            {/* Right Column - Modern Pricing & Booking */}
            <Col xs={24} lg={8} style={{ marginTop: window.innerWidth < 768 ? '32px' : '0px' }}>
              <div style={{ 
                position: window.innerWidth < 768 ? 'sticky' : 'sticky',
                top: window.innerWidth < 768 ? '20px' : '0px',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '24px',
                padding: window.innerWidth < 768 ? '24px' : '32px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.08), 0 8px 25px rgba(0,0,0,0.05)',
                border: '1px solid rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {/* Background Pattern */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(45deg, rgba(243, 156, 18, 0.1) 0%, rgba(231, 76, 60, 0.1) 100%)',
                  borderRadius: '50%',
                  transform: 'translate(30px, -30px)'
                }} />
                
                <div style={{ position: 'relative', zIndex: 1 }}>
                  {/* Header */}
                  <div style={{ marginBottom: '24px' }}>
                    <Title level={3} style={{ 
                      marginBottom: '16px', 
                      color: '#2c3e50',
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      textAlign: 'center'
                    }}>
                      Your Quote
                    </Title>
                    
                    {/* Pricing Card */}
                    <div style={{ 
                      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                      borderRadius: '20px',
                      padding: '28px 24px',
                      marginBottom: '24px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Decorative Elements */}
                      <div style={{
                        position: 'absolute',
                        top: '-20px',
                        right: '-20px',
                        width: '80px',
                        height: '80px',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '50%'
                      }} />
                      
                      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                        {((campaignType === 'flyer-1' && selectedServices.length > 0) || (selectedServices.length <= includedServicesCount && selectedServices.length > 0)) && (
                          <div style={{ 
                            textDecoration: 'line-through', 
                            color: 'rgba(255,255,255,0.6)', 
                            fontSize: window.innerWidth < 768 ? '1.2rem' : '1.4rem',
                            fontWeight: '500',
                            marginBottom: '8px',
                            opacity: 0.8
                          }}>
                            ${selectedServices.length > includedServicesCount ? 
                              (399 + ((selectedServices.length - includedServicesCount) * 50)) : 
                              399}
                          </div>
                        )}
                        
                        <div style={{ 
                          color: 'white', 
                          fontSize: window.innerWidth < 768 ? '2.2rem' : '2.8rem', 
                          fontWeight: '800',
                          fontFamily: theme.fonts.primary,
                          marginBottom: '8px',
                          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}>
                          ${calculateTotal()}
                        </div>
                        
                        {campaignType === 'flyer-1' && (
                          <div style={{
                            display: 'inline-block',
                            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                            color: 'white',
                            padding: '6px 16px',
                            borderRadius: '15px',
                            fontSize: '12px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '12px'
                          }}>
                            Special Price
                          </div>
                        )}
                        
                        {((campaignType === 'flyer-1' && selectedServices.length > 0) || (selectedServices.length <= includedServicesCount && selectedServices.length > 0)) && (
                          <div style={{ 
                            color: '#f39c12', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            marginBottom: '12px'
                          }}>
                            Save ${selectedServices.length > includedServicesCount ? 
                              ((399 + ((selectedServices.length - includedServicesCount) * 50)) - calculateTotal()) : 
                              100}!
                          </div>
                        )}
                        
                        <div style={{ 
                          color: 'rgba(255,255,255,0.8)', 
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          {selectedServices.length > 0 ? `${selectedServices.length} Services Selected` : 'Select Your Services'}
                        </div>
                        
                        {selectedServices.length > includedServicesCount && (
                          <div style={{ 
                            color: 'rgba(255,255,255,0.7)', 
                            fontSize: '12px',
                            fontWeight: '400'
                          }}>
                            Base: ${basePrice} + {selectedServices.length - includedServicesCount} Add-ons (${addOnPrice} each)
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Trust Badges */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: window.innerWidth < 768 ? '1fr 1fr' : '1fr',
                      gap: '12px',
                      marginBottom: '24px'
                    }}>
                      {[
                        { icon: '🛡️', text: 'Licensed & Insured' },
                        { icon: '🔧', text: 'Professional Equipment' },
                        { icon: '⭐', text: '5-Star Rated' }
                      ].map((item, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          background: 'rgba(52, 152, 219, 0.1)',
                          borderRadius: '12px',
                          border: '1px solid rgba(52, 152, 219, 0.2)',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#2c3e50'
                        }}>
                          <span style={{ marginRight: '8px', fontSize: '16px' }}>{item.icon}</span>
                          <span>{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  {selectedServices.length > 0 ? (
              <Button 
                type="primary" 
                size="large"
                onClick={() => {
                  trackCampaignAction('book_now_click', {
                    location: 'pricing_sidebar',
                    selectedServices: selectedServices,
                    totalPrice: calculateTotal(),
                    serviceCount: selectedServices.length
                  });
                  setBookingModalVisible(true);
                }}
                style={{ 
                        height: window.innerWidth < 768 ? '56px' : '64px', 
                        width: '100%',
                        background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                        borderRadius: '16px',
                        fontSize: window.innerWidth < 768 ? '16px' : '18px',
                  fontWeight: '700',
                  fontFamily: theme.fonts.primary,
                        boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                        transition: 'all 0.3s ease',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 12px 35px rgba(39, 174, 96, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 8px 25px rgba(39, 174, 96, 0.4)';
                }}
              >
                      <CalendarOutlined style={{ marginRight: '8px' }} />
                      Book Now
              </Button>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      background: 'rgba(149, 165, 166, 0.1)',
                      borderRadius: '16px',
                      color: '#7f8c8d',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      Select services to see pricing
            </div>
          )}
                </div>
              </div>
            </Col>
          </Row>
            
        </div>

        {/* Modern Benefits Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          padding: '60px 0',
          marginTop: '20px'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <Title level={2} style={{ 
                color: '#2c3e50', 
                fontSize: '2.5rem',
                marginBottom: '16px', 
                fontWeight: '700',
                fontFamily: theme.fonts.primary 
              }}>
                Why Choose HSP?
              </Title>
              <Paragraph style={{ 
                color: theme.colors.gray, 
                fontSize: '1.2rem', 
                fontFamily: theme.fonts.primary,
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Professional home services you can trust
              </Paragraph>
            </div>
            
            <Row gutter={[32, 32]}>
              {[
                {
                  icon: <SafetyOutlined style={{ fontSize: '32px', color: '#27ae60' }} />,
                  title: 'Licensed & Insured',
                  description: 'Fully licensed professionals with comprehensive insurance coverage'
                },
                {
                  icon: <HomeOutlined style={{ fontSize: '32px', color: '#f39c12' }} />,
                  title: 'Professional Equipment',
                  description: 'Fully equipped team with professional-grade tools and supplies'
                },
                {
                  icon: <CheckCircleOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
                  title: 'Satisfaction Guaranteed',
                  description: 'We stand behind our work with a 100% satisfaction guarantee'
                },
                {
                  icon: <ClockCircleOutlined style={{ fontSize: '32px', color: '#9b59b6' }} />,
                  title: 'How We Work',
                  description: 'Learn about our proven process and service methodology',
                  isLink: true,
                  linkTo: '/how-we-work'
                }
              ].map((benefit, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <div 
                    style={{
                      background: 'white',
                      padding: '40px 30px',
                      borderRadius: '20px',
                      textAlign: 'center',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      border: '1px solid #f1f3f4',
                      cursor: benefit.isLink ? 'pointer' : 'default'
                    }}
                    onClick={benefit.isLink ? () => navigate(benefit.linkTo) : undefined}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-5px)';
                      e.target.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                      if (benefit.isLink) {
                        e.target.style.border = '2px solid #9b59b6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                      if (benefit.isLink) {
                        e.target.style.border = '1px solid #f1f3f4';
                      }
                    }}
                  >
                    <div style={{ 
                      marginBottom: '24px',
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      borderRadius: '50%',
                      width: '80px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px'
                    }}>
                      {benefit.icon}
                    </div>
                    <Title level={4} style={{ 
                      marginBottom: '16px', 
                      color: '#2c3e50', 
                      fontSize: '20px', 
                      fontWeight: '700',
                      fontFamily: theme.fonts.primary 
                    }}>
                      {benefit.title}
                    </Title>
                    <Paragraph style={{ 
                      color: theme.colors.gray, 
                      margin: 0, 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      fontFamily: theme.fonts.primary 
                    }}>
                      {benefit.description}
                    </Paragraph>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>


        {/* Modern Final CTA Section */}
        <div style={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          color: 'white',
          padding: '80px 0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }} />
          
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ 
                color: 'white', 
                fontSize: '3rem',
                marginBottom: '20px', 
                fontWeight: '800',
                fontFamily: theme.fonts.primary,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
              }}>
                Ready to Get Started?
              </Title>
              <Paragraph style={{ 
                fontSize: '1.3rem', 
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '50px',
                maxWidth: '700px',
                margin: '0 auto 50px',
                lineHeight: '1.6',
                fontFamily: theme.fonts.primary
              }}>
                Don't miss this limited-time offer. Book now to secure your spot!
              </Paragraph>
              
              <Space size="large" wrap>
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={() => {
                    trackCampaignAction('book_now_click', {
                      location: 'bottom_cta',
                      selectedServices: selectedServices,
                      totalPrice: calculateTotal(),
                      serviceCount: selectedServices.length
                    });
                    setBookingModalVisible(true);
                  }}
                  style={{ 
                    height: '64px', 
                    fontSize: '18px',
                    padding: '0 40px',
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    border: 'none',
                    borderRadius: '50px',
                    fontWeight: '700',
                    fontFamily: theme.fonts.primary,
                    boxShadow: '0 8px 25px rgba(231, 76, 60, 0.4)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 12px 35px rgba(231, 76, 60, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 8px 25px rgba(231, 76, 60, 0.4)';
                  }}
                >
                  <CalendarOutlined style={{ marginRight: '12px', fontSize: '20px' }} />
                  Book Your Services Now
                </Button>
                
                <Button 
                  size="large" 
                  onClick={handleCallNow}
                  style={{ 
                    height: '64px', 
                    fontSize: '18px',
                    padding: '0 40px',
                    background: 'transparent',
                    border: '2px solid white',
                    color: 'white',
                    borderRadius: '50px',
                    fontWeight: '700',
                    fontFamily: theme.fonts.primary,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#2c3e50';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <PhoneOutlined style={{ marginRight: '12px', fontSize: '20px' }} />
                  Call: (888) 504-6944
                </Button>
              </Space>
              
              <div style={{ marginTop: '40px' }}>
                <Text style={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  fontSize: '16px', 
                  fontFamily: theme.fonts.primary 
                }}>
                  * $99 applies to on-site visit covering selected services.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clean Scheduling Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ margin: 0, color: theme.colors.black, fontFamily: theme.fonts.primary }}>
              Schedule Your Services
            </Title>
            <Paragraph style={{ margin: '8px 0 0 0', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
              Schedule your preferred date and time
            </Paragraph>
            <div style={{ 
              background: theme.colors.accent, 
              padding: '8px 16px', 
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.accentDark}`,
              display: 'inline-block',
              marginTop: '12px'
            }}>
              <Text strong style={{ color: theme.colors.black, fontFamily: theme.fonts.primary }}>
                Selected Services: {selectedServices.length} • Total: ${calculateTotal()}
              </Text>
            </div>
          </div>
        }
        open={showSchedulingForm}
        onCancel={() => setShowSchedulingForm(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 600}
        centered
        style={{ 
          zIndex: 99999,
          maxHeight: window.innerWidth < 768 ? '80vh' : 'auto',
          top: window.innerWidth < 768 ? '50%' : 'auto',
          left: window.innerWidth < 768 ? '50%' : 'auto',
          transform: window.innerWidth < 768 ? 'translate(-50%, -50%)' : 'none',
          position: window.innerWidth < 768 ? 'fixed' : 'relative'
        }}
        maskStyle={{ zIndex: 99998 }}
        getContainer={() => document.body}
        bodyStyle={{
          maxHeight: window.innerWidth < 768 ? '65vh' : 'auto',
          overflowY: window.innerWidth < 768 ? 'auto' : 'visible',
          padding: window.innerWidth < 768 ? '0 12px 12px 12px' : '24px'
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            time: dayjs('09:00', 'HH:mm')
          }}
          style={{
            marginBottom: window.innerWidth < 768 ? '0' : 'auto'
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Text strong>Full Name</Text>}
                name="name"
                rules={[{ required: true, message: 'Please enter your full name' }]}
                style={{ marginBottom: window.innerWidth < 768 ? '12px' : '16px' }}
              >
                <Input size={window.innerWidth < 768 ? 'middle' : 'large'} placeholder="Enter your full name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Text strong>Phone Number</Text>}
                name="phone"
                rules={[
                  { required: true, message: 'Please enter your phone number' },
                  { pattern: /^[\d\s\-\+\(\)]+$/, message: 'Please enter a valid phone number' }
                ]}
                style={{ marginBottom: window.innerWidth < 768 ? '12px' : '16px' }}
              >
                <Input size={window.innerWidth < 768 ? 'middle' : 'large'} placeholder="(123) 456-7890" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<Text strong>Email Address</Text>}
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
            style={{ marginBottom: window.innerWidth < 768 ? '12px' : '16px' }}
          >
            <Input size={window.innerWidth < 768 ? 'middle' : 'large'} placeholder="your@email.com" />
          </Form.Item>

          <Form.Item
            label={<Text strong>Property Address</Text>}
            name="address"
            rules={[{ required: true, message: 'Please enter your property address' }]}
            style={{ marginBottom: window.innerWidth < 768 ? '12px' : '16px' }}
          >
            <div style={{ position: 'relative' }}>
              <Input
                ref={addressInputRef}
                size={window.innerWidth < 768 ? 'middle' : 'large'}
                placeholder="123 Main St, City, State 12345"
                onChange={handleAddressChange}
                onFocus={handleAddressFocus}
                onBlur={handleAddressBlur}
                loading={addressLoading}
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                >
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: index < addressSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                        fontSize: '14px',
                        color: '#262626'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'white';
                      }}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Text strong>Preferred Date</Text>}
                name="date"
                rules={[{ required: true, message: 'Please select a date' }]}
                style={{ marginBottom: window.innerWidth < 768 ? '12px' : '16px' }}
              >
                <DatePicker 
                  size={window.innerWidth < 768 ? 'middle' : 'large'} 
                  style={{ width: '100%' }}
                  suffixIcon={<CalendarOutlined style={{ cursor: 'pointer' }} />}
                  allowClear={false}
                  getPopupContainer={(trigger) => trigger.parentElement}
                  disabledDate={(current) => {
                    // No date restrictions - allow any date selection
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Text strong>Preferred Time</Text>}
                name="time"
                rules={[{ required: true, message: 'Please select a time' }]}
              >
                <TimePicker 
                  size="large" 
                  style={{ width: '100%' }}
                  format="HH:mm"
                  minuteStep={30}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<Text strong>Special Instructions (Optional)</Text>}
            name="instructions"
          >
            <Input.TextArea 
              rows={window.innerWidth < 768 ? 2 : 3} 
              placeholder="Any special requests or notes about your property..."
            />
          </Form.Item>

          {paymentError && (
            <div style={{ marginBottom: '16px' }}>
              <Alert
                message="Payment Error"
                description={paymentError}
                type="error"
                showIcon
                closable
                onClose={() => setPaymentError(null)}
              />
            </div>
          )}

          <div style={{ 
            textAlign: 'center', 
            marginTop: window.innerWidth < 768 ? '16px' : '24px' 
          }}>
            <Space 
              size="large" 
              direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
              style={{ width: window.innerWidth < 768 ? '100%' : 'auto' }}
            >
              <Button 
                size="large" 
                onClick={() => setShowSchedulingForm(false)}
                style={{ 
                  padding: '0 24px',
                  width: window.innerWidth < 768 ? '100%' : 'auto'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                size="large"
                htmlType="submit"
                loading={isProcessingPayment}
                style={{ 
                  padding: '0 24px',
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  border: 'none',
                  fontWeight: '600',
                  fontFamily: theme.fonts.primary,
                  width: window.innerWidth < 768 ? '100%' : 'auto'
                }}
              >
                <DollarOutlined style={{ marginRight: '8px' }} />
                Pay ${calculateServicePrice(selectedServices, campaignType)} - Secure Checkout
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Signup Prompt Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ margin: 0, color: theme.colors.black, fontFamily: theme.fonts.primary }}>
              🎉 Request Submitted Successfully!
            </Title>
            <Paragraph style={{ margin: '8px 0 0 0', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
              Your service request has been received
            </Paragraph>
          </div>
        }
        open={showSignupPrompt}
        onCancel={() => {
          setShowSignupPrompt(false);
          form.resetFields();
          setSelectedServices([]);
        }}
        footer={null}
        width={500}
        centered
        closable={false}
        style={{ zIndex: 99999 }}
        maskStyle={{ zIndex: 99998 }}
        getContainer={() => document.body}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ marginBottom: '24px' }}>
            <Text style={{ fontSize: '16px', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
              <strong>Quote ID:</strong> {submittedQuoteId?.substring(0, 8)}...
            </Text>
          </div>
          
          <div style={{ 
            background: theme.colors.accent, 
            padding: '20px', 
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.accentDark}`,
            marginBottom: '24px'
          }}>
            <Title level={4} style={{ color: theme.colors.black, fontFamily: theme.fonts.primary, marginBottom: '12px' }}>
              Track Your Service Request
            </Title>
            <Paragraph style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary, marginBottom: '16px' }}>
              Sign in with Google to track your service status, receive notifications, and manage your appointments.
            </Paragraph>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ color: theme.colors.primary }} />
                <Text style={{ fontFamily: theme.fonts.primary }}>Real-time status updates</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ color: theme.colors.primary }} />
                <Text style={{ fontFamily: theme.fonts.primary }}>Email & SMS notifications</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <CheckCircleOutlined style={{ color: theme.colors.primary }} />
                <Text style={{ fontFamily: theme.fonts.primary }}>Service history & receipts</Text>
              </div>
            </div>
          </div>
          
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Google Sign-In Button */}
            <Button
              onClick={handleGoogleSignIn}
              loading={isSigningIn}
              size="large"
              style={{
                height: '56px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                background: theme.colors.white,
                border: '2px solid #4285f4',
                color: '#4285f4',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: theme.borderRadius.md,
                boxShadow: theme.shadows.sm,
                fontFamily: theme.fonts.primary,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f8f9ff';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = theme.shadows.md;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.colors.white;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = theme.shadows.sm;
              }}
            >
              <GoogleOutlined style={{ fontSize: '20px' }} />
              Sign in with Google to Track Your Service
            </Button>
            
            {/* Skip Button */}
            <Button 
              type="text"
              size="large"
              onClick={() => {
                trackCampaignAction('signup_prompt_click', { 
                  action: 'skip_signup',
                  quoteId: submittedQuoteId,
                  source: 'campaign_landing'
                });
                setShowSignupPrompt(false);
                form.resetFields();
                setSelectedServices([]);
              }}
              style={{ 
                padding: '0 24px',
                color: theme.colors.gray,
                fontFamily: theme.fonts.primary,
                fontSize: '14px'
              }}
            >
              Skip for now - I'll track via phone
            </Button>
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <Text style={{ fontSize: '14px', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
              We'll contact you at the phone number provided to confirm your appointment.
            </Text>
          </div>
        </div>
      </Modal>

      {/* Mobile Sticky Footer - Only for flyer-1 campaign */}
      {campaignType === 'flyer-1' && (
        <div 
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
            padding: '16px 20px',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            zIndex: 1000,
            display: window.innerWidth < 768 ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Text style={{ 
              color: 'white', 
              fontSize: '18px', 
              fontWeight: '700',
              fontFamily: theme.fonts.primary,
              margin: 0
            }}>
              ${calculateTotal()}
            </Text>
            <Text style={{ 
              color: 'rgba(255,255,255,0.8)', 
              fontSize: '12px',
              fontFamily: theme.fonts.primary,
              margin: 0
            }}>
              {selectedServices.length} services selected
            </Text>
          </div>
          
          <Button
            type="primary"
            size="large"
            onClick={() => setBookingModalVisible(true)}
            disabled={selectedServices.length === 0}
            style={{
              background: selectedServices.length > 0 
                ? 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)' 
                : '#6c757d',
              border: 'none',
              borderRadius: '12px',
              height: '48px',
              padding: '0 24px',
              fontFamily: theme.fonts.primary,
              fontWeight: '700',
              fontSize: '16px',
              boxShadow: selectedServices.length > 0 
                ? '0 4px 15px rgba(39, 174, 96, 0.4)' 
                : 'none',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              minWidth: '120px'
            }}
          >
            {selectedServices.length > 0 ? 'Book Now' : 'Select Services'}
          </Button>
        </div>
      )}

      {/* New Booking Modal */}
      <BookingModal 
        visible={bookingModalVisible} 
        onClose={() => setBookingModalVisible(false)} 
        source="flyer_1_campaign_booking" 
      />
    </>
  );
};

export default FreeInspectWeedPage;